import type { TourDefinition, TourPath, TourStep } from '@pantry/shared';

/**
 * Keep shared steps plus path-specific steps for the selected path.
 */
export function getPathFilteredSteps(
  definition: TourDefinition | undefined,
  path: TourPath | null
): TourStep[] {
  const steps = definition?.steps.web;
  if (!steps) return [];

  return steps.filter((step) => !step.path || step.path === path) as TourStep[];
}

/**
 * Resolve a deterministic path for branching tours.
 * Prefer explicit server state, then infer from completed path-specific steps.
 */
export function resolveTourPath(
  definition: TourDefinition | undefined,
  selectedPath: TourPath | null | undefined,
  completedSteps: string[]
): TourPath | null {
  if (selectedPath) return selectedPath;

  const steps = definition?.steps.web;
  if (!steps?.length || completedSteps.length === 0) return null;

  const completedSet = new Set(completedSteps);

  for (let i = steps.length - 1; i >= 0; i--) {
    const step = steps[i];
    if (step.path && completedSet.has(step.id)) {
      return step.path;
    }
  }

  return null;
}

/**
 * Convert a full-definition index to the equivalent index in a path-filtered list.
 */
export function mapFullIndexToFilteredIndex(
  definition: TourDefinition | undefined,
  fullIndex: number,
  path: TourPath | null
): number {
  const steps = definition?.steps.web;
  if (!steps?.length) return 0;

  const includedFullIndices = steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => !step.path || step.path === path)
    .map(({ index }) => index);

  if (includedFullIndices.length === 0) return 0;

  const normalizedFullIndex = Math.max(0, fullIndex);
  const nextIncludedIndex = includedFullIndices.findIndex((index) => index >= normalizedFullIndex);

  if (nextIncludedIndex !== -1) {
    return nextIncludedIndex;
  }

  return includedFullIndices.length - 1;
}

/**
 * Get steps before the current full index, filtered by path.
 */
export function getStepsBeforeIndex(
  definition: TourDefinition | undefined,
  fullIndex: number,
  path: TourPath | null
): TourStep[] {
  const steps = definition?.steps.web;
  if (!steps?.length) return [];

  return steps
    .map((step, index) => ({ step, index }))
    .filter(({ step, index }) => index < fullIndex && (!step.path || step.path === path))
    .map(({ step }) => step as TourStep);
}
